// src/components/InvoiceDetails.jsx
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "../axiosConfig";
import { FaRegCalendarAlt } from "react-icons/fa";
import "../styles/InvoiceDetails.css";
import { useTranslation } from "react-i18next";

/* ============ Helpers ============ */
function formatDate(dateLike) {
  if (!dateLike) return "";
  const d = new Date(dateLike);
  if (Number.isNaN(d)) return "";
  return d.toLocaleDateString();
}

function addDays(dateLike, days = 0) {
  if (!dateLike && days == null) return null;
  const d = new Date(dateLike || Date.now());
  if (Number.isNaN(d)) return null;
  const n = Number(days || 0);
  d.setDate(d.getDate() + n);
  return d;
}

function formatMoney(value, currencySymbol = "€") {
  if (value == null || value === "") return "";
  const num = typeof value === "string" ? Number(value) : value;
  if (Number.isNaN(num)) return String(value);
  try {
    return new Intl.NumberFormat("pt-PT", {
      style: "currency",
      currency: "EUR"
    }).format(num);
  } catch {
    return `${Number(num).toFixed(2)} ${currencySymbol}`;
  }
}

function normalizeClientType(v) {
  if (!v) return "";
  if (typeof v === "object") return v.label || v.value || "";
  const s = String(v).toLowerCase();
  if (s === "industry") return "Industry";
  if (s === "carrier") return "Carrier";
  return v;
}

function normalizeStatus(v) {
  if (!v) return "";
  if (typeof v === "object") return v.label || v.value || "";
  const map = {
    draft: "draft",
    open: "open",
    paid: "paid",
    cancelled: "cancelled",
    void: "void",
    sent: "sent",
    partial: "partial"
  };
  const key = String(v).toLowerCase();
  return map[key] || key;
}

function computePaymentExpiration(invoice, t) {
  if (invoice?.paymentDueDate) {
    return formatDate(invoice.paymentDueDate);
  }
  if (invoice?.issueDate && (invoice?.expiryDays || invoice?.expiryOption?.value)) {
    const days =
      invoice.expiryDays ??
      (typeof invoice.expiryOption?.value === "number"
        ? invoice.expiryOption.value
        : Number(invoice.expiryOption?.value));
    const due = addDays(invoice.issueDate, days || 0);
    return due ? formatDate(due) : "";
  }
  if (invoice?.expiryOption?.label) return invoice.expiryOption.label;
  if (invoice?.expiryLabel) return invoice.expiryLabel;

  if (invoice?.expiryDays != null) {
    const n = Number(invoice.expiryDays) || 0;
    // i18n pluralization
    return t("fields.payment_expiration_days", { count: n });
  }
  return "";
}

function normalizePrice(invoice) {
  const raw =
    invoice?.price ??
    invoice?.total ??
    invoice?.totalPrice ??
    invoice?.amount ??
    invoice?.gross ??
    null;
  return formatMoney(raw);
}

/** ---------- Cartão de Detalhes ---------- */
function InvoiceDetailsCard({ invoice, onClose }) {
  const { t } = useTranslation("invoice_details");
  if (!invoice) return null;

  const invoiceId = invoice.invoiceId ?? invoice.id ?? "";
  const clientName = invoice.clientName ?? invoice.client ?? "";
  const clientTypeDisplay = normalizeClientType(invoice.clientType);
  const statusKey = normalizeStatus(invoice.status);

  const pickupPlace =
    invoice.pickupPlace ??
    invoice.pickup ??
    invoice.pickupName ??
    "";
  const deliveryPlace =
    invoice.deliveryPlace ??
    invoice.delivery ??
    invoice.deliveryName ??
    "";

  const priceDisplay = normalizePrice(invoice);

  const formattedIssueDate = formatDate(invoice.issueDate ?? invoice.date);
  const paymentExpiration = computePaymentExpiration(invoice, t);

  const statusText = t(`status.${statusKey}`, statusKey);

  return (
    <div className="invoice-details-container">
      <h2 className="invoice-details-title">{t("title")}</h2>

      <div className="invoice-details-form">
        {/* Top: Invoice ID */}
        <div
          className="invoice-details-form-top"
          style={{ display: "flex", justifyContent: "flex-start" }}
        >
          <div className="invoice-details-id">
            <span className="invoice-details-id-label">{t("fields.invoice_id")} </span>
            <span className="invoice-details-id-value">{invoiceId}</span>
          </div>
        </div>

        {/* Row 1 */}
        <div
          className="invoice-details-row"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0,1fr))",
            gap: 16
          }}
        >
          <div>
            <label className="invoice-details-label">{t("fields.client_name")}</label>
            <div className="invoice-details-field">{clientName || "—"}</div>
          </div>
          <div>
            <label className="invoice-details-label">{t("fields.client_type")}</label>
            <div className="invoice-details-field">
              {t(`client_type.${clientTypeDisplay}`, clientTypeDisplay || "—")}
            </div>
          </div>
          <div>
            <label className="invoice-details-label">{t("fields.status")}</label>
            <div className="invoice-details-field">{statusText || "—"}</div>
          </div>
        </div>

        {/* Row 2 */}
        <div
          className="invoice-details-row"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0,1fr))",
            gap: 16
          }}
        >
          <div>
            <label className="invoice-details-label">{t("fields.pickup_place")}</label>
            <div className="invoice-details-field">{pickupPlace || "—"}</div>
          </div>
          <div>
            <label className="invoice-details-label">{t("fields.delivery_place")}</label>
            <div className="invoice-details-field">{deliveryPlace || "—"}</div>
          </div>
        </div>

        {/* Row 3 */}
        <div
          className="invoice-details-row"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0,1fr))",
            gap: 16
          }}
        >
          <div>
            <label className="invoice-details-label">{t("fields.price")}</label>
            <div className="invoice-details-field">{priceDisplay || "—"}</div>
          </div>
          <div>
            <label className="invoice-details-label">{t("fields.issue_date")}</label>
            <div className="invoice-details-field invoice-details-date-icon-field">
              <span>{formattedIssueDate || "—"}</span>
              <FaRegCalendarAlt className="invoice-details-calendar-icon" />
            </div>
          </div>
          <div>
            <label className="invoice-details-label">{t("fields.payment_expiration")}</label>
            <div className="invoice-details-field">
              {paymentExpiration || "—"}
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="invoice-details-buttons">
          <button onClick={onClose} className="invoices-close-details-btn">
            {t("buttons.close")}
          </button>
          <a
            href={`/invoices/edit/${invoiceId}`}
            className="invoices-edit-details-btn"
            onClick={(e) => {
              e.preventDefault();
              window.history.pushState({}, "", `/invoices/edit/${invoiceId}`);
              window.dispatchEvent(new PopStateEvent("popstate"));
            }}
          >
            {t("buttons.edit")}
          </a>
        </div>
      </div>
    </div>
  );
}

/** ---------- Página ---------- */
export default function InvoiceDetails() {
  const { t } = useTranslation("invoice_details");
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state?.prefill;

  const [invoice, setInvoice] = useState(prefill || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let ignore = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const { data } = await axios.get(`/invoices/${invoiceId}`);
        if (!ignore) {
          setInvoice(prev => ({ ...(prev || {}), ...(data || {}) }));
        }
      } catch (e) {
        if (!ignore) setError(t("messages.error_load"));
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => { ignore = true; };
  }, [invoiceId, t]);

  if (loading)
    return (
      <div className="invoice-details-container">
        <div className="invoice-details-form">{t("messages.loading")}</div>
      </div>
    );
  if (error)
    return (
      <div className="invoice-details-container">
        <div className="invoice-details-form">{error}</div>
      </div>
    );
  if (!invoice)
    return (
      <div className="invoice-details-container">
        <div className="invoice-details-form">{t("messages.not_found")}</div>
      </div>
    );

  return (
    <div style={{ minHeight: "100vh", background: "#0e0f12", padding: "32px 16px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <InvoiceDetailsCard
          invoice={invoice}
          onClose={() => navigate("/invoices")}
        />
      </div>
    </div>
  );
}
